import { Component, OnInit } from '@angular/core';
import { Observable, combineLatest, distinctUntilChanged, map } from 'rxjs';

import { ModeNamesEnum } from 'app/modules/browse/models/browse-mode';
import { BrowseTypesEnum } from 'app/modules/browse/models/browse-types.enum';
import { CarouselItems } from 'app/modules/browse/models/carousel-items.enum';
import { Playlist } from '@kinomap-dev/ng-ui-lib/lib/models/playlist.model';
import { FavoriteStore } from 'app/modules/browse/store/favorite/favorite.store';
import { ScrollingService } from 'app/services/scrolling.service';
import { TranslateModule } from '@ngx-translate/core';
import { ListingComponent } from '../../../components/listing/listing.component';
import { BackButtonComponent } from '../../../components/back-button/back-button.component';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { BrowseCarouselComponent } from '../../../components/carousels/browse-carousel/browse-carousel.component';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import {
  Coaching,
  GpsOnly,
  HeaderBlockComponent,
  NoDataBlockComponent,
  User,
  VideoSection,
  Workout,
} from '@kinomap-dev/ng-ui-lib';
import { Store } from '@ngrx/store';
import { selectProfileData } from 'app/store/me/me.selectors';
import { SwarmLoginService } from 'app/services';

@Component({
  selector: 'app-catalog-library',
  templateUrl: './catalog-library.component.html',
  styleUrls: ['./catalog-library.component.scss'],
  standalone: true,
  imports: [
    NgTemplateOutlet,
    BrowseCarouselComponent,
    LoaderComponent,
    BackButtonComponent,
    ListingComponent,
    AsyncPipe,
    TranslateModule,
    NoDataBlockComponent,
    HeaderBlockComponent,
  ],
})
export class CatalogLibraryComponent implements OnInit {
  ModeNames: typeof ModeNamesEnum = ModeNamesEnum;
  ContentTypes: typeof BrowseTypesEnum = BrowseTypesEnum;
  CarouselItem: typeof CarouselItems = CarouselItems;
  displayMode: BrowseTypesEnum | 'all' = 'all';
  videos$: Observable<VideoSection[]>;
  gpsTracks$: Observable<GpsOnly[]>;
  workouts$: Observable<Workout[]>;
  coachings$: Observable<Coaching[]>;
  playlists$: Observable<Playlist[]>;
  isLoading$: Observable<boolean>;
  hasFavorites$: Observable<boolean>;
  clientHasLog$: Observable<boolean> = this.store
    .select(selectProfileData)
    .pipe(
      map((user: User) => {
        return user !== null;
      })
    );

  constructor(
    private favoriteStore: FavoriteStore,
    private scrollingService: ScrollingService,
    private store: Store,
    public loginService: SwarmLoginService
  ) { }

  ngOnInit(): void {
    this.videos$ = this.favoriteStore.videos$.pipe(
      map((videos: VideoSection[]) => {
        return videos.slice(0, 10);
      })
    );

    this.gpsTracks$ = this.favoriteStore.gpsTracks$.pipe(
      map((gpsTracks: GpsOnly[]) => {
        return gpsTracks.slice(0, 10);
      })
    );

    this.workouts$ = this.favoriteStore.workouts$.pipe(
      map((workouts: Workout[]) => {
        return workouts.slice(0, 10);
      })
    );

    this.coachings$ = this.favoriteStore.coachings$.pipe(
      map((coachings: Coaching[]) => {
        return coachings.slice(0, 10);
      })
    );

    this.playlists$ = this.favoriteStore.playlists$.pipe(
      map((playlists) => {
        return playlists.slice(0, 10);
      })
    );

    this.isLoading$ = combineLatest([
      this.favoriteStore.isLoadingVideos$,
      this.favoriteStore.isLoadingGpsTracks$,
      this.favoriteStore.isLoadingWorkouts$,
      this.favoriteStore.isLoadingCoachings$,
      this.favoriteStore.isLoadingPlaylists$,
    ]).pipe(
      map(
        ([
          isLoadingVideos,
          isLoadingGpsTracks,
          isLoadingWorkouts,
          isLoadingCoachings,
          isLoadingPlaylists,
        ]) => {
          return (
            isLoadingVideos ||
            isLoadingGpsTracks ||
            isLoadingWorkouts ||
            isLoadingCoachings ||
            isLoadingPlaylists
          );
        }
      ),
      distinctUntilChanged()
    );

    this.hasFavorites$ = combineLatest([
      this.videos$,
      this.gpsTracks$,
      this.workouts$,
      this.coachings$,
      this.playlists$,
    ]).pipe(
      map(([videos, gpsTracks, workouts, coachings, playlists]) => {
        return (
          videos.length > 0 ||
          gpsTracks.length > 0 ||
          workouts.length > 0 ||
          coachings.length > 0 ||
          playlists.length > 0
        );
      }),
      distinctUntilChanged()
    );
  }

  onSwitchDisplayMode(newDisplayMode: BrowseTypesEnum | 'all'): void {
    this.displayMode = newDisplayMode;
    this.scrollingService.detectChangesAndScrollSmoothlyTo('scrollLandmark');
  }
}
